import { Organization } from "@packages/client";
import client from "Libs/platform";

import { isJson, sleep } from "Libs/utils";
import { organizationByNameOrIdSelector } from "Reducers/organization/organization";

import { createAppAsyncThunk } from "Store/createAppAsyncThunk";

const isLegitimateOrganization = (
  organization: Partial<Organization> | undefined
) => !!organization?._links;

export type GetOrganizationProps = {
  organizationDescriptionId: string;
};

/**
 * A just-created organization can be missing from the API replica that serves
 * the first request after sign-up (replication lag). We retry the lookup a few
 * times before treating the organization as "not found", so the registration
 * landing stops intermittently showing the organization error page (CON-1053).
 */
const MAX_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;

/**
 * Returns the insufficient-authentication error payload when the client error
 * means the user needs MFA or SSO to access the organization. This is a
 * definitive answer, so the caller must reject immediately instead of retrying.
 */
const getInsufficientAuthError = (error: unknown) => {
  if (isJson(error)) {
    const errorAsJSON = JSON.parse(error as string);
    if (errorAsJSON.error === "insufficient_user_authentication") {
      // The client doesn't give us a status, so patch it for the error page.
      errorAsJSON.status = 401;
      return errorAsJSON;
    }
  }
  return undefined;
};

/**
 * Returns true when the error is a definitive server answer that retrying
 * won't fix. Network failures (Error instances) and responses without a known
 * status are assumed retryable; other HTTP errors (403, 500, etc.) are not.
 *
 * The client rejects with a parsed object for JSON error responses, a string
 * for text responses, or an Error for network failures. We can only detect
 * status when the API includes it in the JSON body.
 */
const isNonRetryableError = (error: unknown): boolean => {
  if (error instanceof Error) return false;

  if (error && typeof error === "object") {
    const status = (error as Record<string, unknown>).status;
    if (typeof status === "number" && status !== 404) return true;
  }

  return false;
};

/**
 * Builds the redirect path when the URL addresses the organization by its id
 * but the console displays it by name.
 */
const buildRedirectPath = (
  descriptionId: string,
  orgName: string | undefined
): string | undefined => {
  if (orgName && orgName !== descriptionId) {
    return location.pathname.replace(descriptionId, orgName);
  }
  return undefined;
};

/**
 * Loads the organization on the redux store.
 *
 * This function will attempt to load the organization by id first, if it fails
 * to find it, will attemp to find it by name. Therefore is valid to pass an
 * oganization id or a name to this function.
 *
 * Dispatch it from a thunk or a listener, then read the result with a
 * selector:
 *
 * ```ts
 * const orgid = 'the-id-or-name';
 * await dispatch(getOrganization({organizationDescriptionId: orgid });
 * const organization = organizationByNameOrIdSelector(getState(), {organizationNameOrId: orgid });
 * ```
 *
 * In a component, read the organization with a selector. Do NOT dispatch this
 * from a `useEffect` to load it: that scatters the "when to fetch" decision
 * across views and has no place to recover from a transient failure (the very
 * bug CON-1053 came from). Trigger the load from Redux (a thunk or a
 * `listenerMiddleware` entry) instead.
 *
 * ```ts
 * const organization = useAppSelector(
 *   state => organizationByNameOrIdSelector(state, {organizationNameOrId: orgid})
 * );
 * ```
 * @param string organizationDescriptionId the organization id or name
 */
export const getOrganization = createAppAsyncThunk(
  "app/organizations/get_organization",
  async (
    { organizationDescriptionId }: GetOrganizationProps,
    { rejectWithValue, getState }
  ) => {
    /**
     * Case 1
     * The organization is already in the redux state. `orgByDescriptionField`
     * is keyed by name only, but `organizationByNameOrIdSelector` scans
     * cached values for a matching name or id, so a description id coming
     * from the url still resolves without hitting the API.
     */
    const cachedOrganization = organizationByNameOrIdSelector(getState(), {
      organizationNameOrId: organizationDescriptionId
    });

    if (isLegitimateOrganization(cachedOrganization)) {
      const redirectTo = buildRedirectPath(
        organizationDescriptionId,
        cachedOrganization?.name
      );

      return redirectTo
        ? { organization: cachedOrganization, redirectTo }
        : { organization: cachedOrganization };
    }

    /**
     * Cases 2-4
     * Resolve the organization through the API. We retry because a freshly
     * created organization can be temporarily absent from the replica.
     */
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      let organization: Organization | undefined;

      /**
       * Case 2
       * The provided "organizationDescriptionId" is actually the organization id and the organization name is unrelated to the organization id
       *
       * Case 3
       * The provided "organizationDescriptionId" is actually the organization id and the organization name is the lowercase version of the organization.id
       */
      try {
        organization = await client.getOrganization(organizationDescriptionId);
      } catch (error) {
        const authError = getInsufficientAuthError(error);
        if (authError) {
          return rejectWithValue({ error: authError });
        }
        if (isNonRetryableError(error)) {
          return rejectWithValue({ error: "organization.notfound" });
        }
      }

      if (isLegitimateOrganization(organization)) {
        const redirectTo = buildRedirectPath(
          organizationDescriptionId,
          organization?.name
        );

        return redirectTo ? { organization, redirectTo } : { organization };
      }

      /**
       * Case 4
       * The provided "organizationDescriptionId" is the organization name, but it is not in the redux state
       *
       * For admins, they may not have the organization in their /organizations response
       */
      try {
        organization = await client.getOrganization(
          `name=${organizationDescriptionId}`
        );
      } catch (error) {
        const authError = getInsufficientAuthError(error);
        if (authError) {
          return rejectWithValue({ error: authError });
        }
        if (isNonRetryableError(error)) {
          return rejectWithValue({ error: "organization.notfound" });
        }
      }

      if (isLegitimateOrganization(organization)) {
        return { organization };
      }

      /**
       * Not found yet. Wait for replication to catch up, then retry. The delay
       * grows with each attempt and we skip it after the final attempt.
       */
      if (attempt < MAX_ATTEMPTS - 1) {
        await sleep(RETRY_DELAY_MS * (attempt + 1));
      }
    }

    /**
     * Case 5
     * The organization still can't be found after retrying.
     */
    return rejectWithValue({
      error: "organization.notfound"
    });
  }
);
